import {InfoIcon} from "@phosphor-icons/react";
import {Duration} from "luxon";
import {NumberInput, Paper, Switch, Title, InputLabel, Text} from "@mantine/core";
import {LineChart} from "@mantine/charts";
import {useMemo, useState} from "react";
import {Col, Row} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon";
import IconMap from "../../components/Icon/IconMap";
import Label from "../../components/Label/Label";
import {chartPalette} from "./chartUtils";
import {useEntityTypes} from "../../utils/api";
import {clamp, lerp} from "../map/utils";
import allMaterials from './materials.json';

// todo: add material type to database and remove materials.json
const materials = allMaterials.filter(m => m.type !== 'medical' && m.type !== 'gas');
const types = ['heavy', 'light', 'crystal'];
const totRarity = {
    'heavy': 0,
    'light': 0,
    'crystal': 0,
};
materials.forEach(m => {
    types.forEach(t => {
        totRarity[t] += m.type === t ? m.rarity*10 : m.rarity;
    })
});

const calculateTimeScale = (co, piloting, perception) => {
    return (1 - (0.05 * co)) * (1 - (0.05 * piloting)) * (1 - (0.05 * perception));
}
const calculateChance = (sensors, co) => {
    return clamp(sensors * (co / 25), 10, 96);
}

const calculateMPSF = (combat, co) => {
    return 1 + (((combat + co) * 3) / 100);
}
const calculateRMChance = (rm, type) => {
    return (rm.type === type ? rm.rarity*10 : rm.rarity)/totRarity[type];
}
const calculateMaxYield = (rarity, power, mpsf) => {
    return Math.floor(11.5 * rarity * ((power * mpsf) / 1000))
}
const formatter = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0});
const percentage = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});


const BASE_MINING_POWER = 300;


const actions = [1,2,3,4,5,6,7,8,9,10];
const calculateCDF = (actions, spaceCommand, management, piloting) => {
    let psf = 1- ((piloting*2)/100);
    let skill = 1 - (((spaceCommand + management)*2)/100);

    if(actions === 1) {
        return 0.8 * psf;
    } else if (actions<=3) {
        return psf;
    } else {
        return (actions/3) * skill * psf;
    }
}
const miningTimeYDomain = [0, (dataMax) => Math.max(250, Math.ceil(dataMax))];
const miningTimeSeries = [{name: 'time', label: 'Mining Time', color: chartPalette[0]}];


const calculatePriceUnit = (material, power, mpsf) => {
    const actualYield = calculateMaxYield(material.rarity, power, mpsf)
    const maxTheoryYield = calculateMaxYield(material.rarity, BASE_MINING_POWER, mpsf)

    return material.price * 1.55 * lerp(1, 0.75, clamp(actualYield/maxTheoryYield, 0, 1))
}


export default function AFProspecting() {
    const {ships} = useEntityTypes(['ships']);

    const prospectingShips = useMemo(() => ships.filter(s => s.asteroidMining?.prospecting > 0), [ships]);
    const miningShips = useMemo(() => ships.filter(s => s.asteroidMining?.mining > 0), [ships]);

    const [co, setCo] = useState(0);
    const [piloting, setPiloting] = useState(0);
    const [combat, setCombat] = useState(0);
    const [perc, setPerc] = useState(0);
    const [spaceCommand, setSpaceCommand] = useState(0);
    const [management, setManagement] = useState(0);

    const [mineBySquad, setMineBySquad] = useState(false);


    const scale = useMemo(() => calculateTimeScale(co, piloting, perc), [co, piloting, perc]);
    const mpsf = useMemo(() => calculateMPSF(combat, co), [combat, co]);


    const chartMiningTime = useMemo(() => actions.map(a => ({
        actions: a,
        time: 72 * calculateCDF(a, spaceCommand, management, piloting),
    })), [piloting, spaceCommand, management]);

    return (
        <div>
            <title>Asteroid Field Prospecting Charts</title>
            <Row align="center">
                <Col><h1>AF Prospecting and Mining</h1></Col>
                <Col grow={1}/>
                <Col><a className="link no-underline" href="https://www.swcombine.com/rules/?Asteroid_Mining" target="_blank" rel="noreferrer"><Icon
                    icon={IconMap.SwcRules}/> Full rules</a></Col>
            </Row>
            <Paper mb={10}>
                <Title order={2}  mt={0}>Prospecting</Title>
                <Row mb={2}>
                    <Col>
                        <InputLabel>Computer Operation</InputLabel>
                        <NumberInput value={co} onChange={setCo} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <InputLabel>Freighter Piloting</InputLabel>
                        <NumberInput value={piloting} onChange={setPiloting} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <InputLabel>Perception</InputLabel>
                        <NumberInput value={perc} onChange={setPerc} min={0} max={5} style={{width: 80}}/>
                    </Col>
                </Row>

                <table className="w-full">
                    <thead>
                    <tr>
                        <th>Ships</th>
                        <th>Total Sensors</th>
                        <th>Prospecting time</th>
                        <th>Re-prospecting time</th>
                        <th>Chance of revealing</th>
                    </tr>
                    </thead>
                    <tbody>
                    {prospectingShips.map(ship => {
                        let pT = Duration.fromObject({hours: 4 * scale}).shiftTo('hours', 'minutes', 'seconds');
                        let rT = Duration.fromObject({hours: 1 * scale}).shiftTo('hours', 'minutes', 'seconds');
                        const single = (
                            <tr key={ship.id}>
                                <th>{ship.name}</th>
                                <th>{ship.asteroidMining.prospecting}</th>
                                <td>{pT.toHuman({})}</td>
                                <td>{rT.toHuman()}</td>
                                <td>{percentage.format(calculateChance(ship.asteroidMining.prospecting, co))}%</td>
                            </tr>
                        );
                        if(ship.slotsize <= 6) {
                            const multi = Math.floor(12/ship.slotsize);
                            const sensors = ship.asteroidMining.prospecting*multi;
                            return (
                                <>
                                    {single}
                                    <tr key={ship.id+"-squad"}>
                                        <th>{ship.name} (Squad)</th>
                                        <th>{sensors}</th>
                                        <td>{pT.toHuman({})}</td>
                                        <td>{rT.toHuman()}</td>
                                        <td>{percentage.format(calculateChance(sensors, co))}%</td>
                                    </tr>
                                </>
                            )
                        }
                        return single;
                    })}
                    </tbody>
                </table>
            </Paper>
            <Paper mb={10}>
                <Title order={2}>Deposit Chances</Title>
                <table className="w-full">
                    <thead>
                    <tr>
                        <th>Material</th>
                        <th>Rarity</th>
                        <th>Type</th>
                        <th>Heavy Metal Field</th>
                        <th>Light Metal Field</th>
                        <th>Crystal Field</th>
                    </tr>
                    </thead>
                    <tbody>
                    {materials.map(rm => <RMChanceRow key={rm.typeID} rm={rm}/>)}
                    </tbody>
                </table>
            </Paper>
            <Paper mb={10}>
                <Title order={2} mt={0}>Deposit Sizes and Yields</Title>
                <Row mb={2}>
                    <Col>
                        <Label>Computer Operation</Label>
                        <NumberInput value={co} onChange={setCo} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>Freighter Combat</Label>
                        <NumberInput value={combat} onChange={setCombat} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>By Squad</Label>
                        <Switch checked={mineBySquad} onChange={e => setMineBySquad(e.currentTarget.checked)}/>
                    </Col>
                </Row>
                <table className="w-full">
                    <thead>

                    <tr>
                        <th>Material</th>
                        <th>Rarity</th>
                        <th>Min Size</th>
                        <th>Max Size</th>
                        <th colSpan={miningShips.length}>Max yield</th>
                    </tr>
                    <tr>
                        <th colSpan={4}/>
                        {miningShips.map(s => <td key={s.id} className="text-right">{s.name}</td>)}
                    </tr>
                    </thead>
                    <tbody>
                    {materials.map(rm => <RMRow key={rm.typeID} rm={rm} mpsf={mpsf} ships={miningShips} bySquad={mineBySquad}/>)}
                    </tbody>
                </table>
            </Paper>
            <Paper mb={10}>
                <Title order={2}>Mining Cost</Title>
                <Text mt={2} mb={2}>
                    <InfoIcon color="var(--mantine-color-blue-5)" />     This assumes always mining at full yield. It does not account for partial yields due to cargo space.
                </Text>
                <Row mb={2}>
                    <Col>
                        <Label>Computer Operation</Label>
                        <NumberInput value={co} onChange={setCo} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>Freighter Combat</Label>
                        <NumberInput value={combat} onChange={setCombat} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>By Squad</Label>
                        <Switch checked={mineBySquad} onChange={e => setMineBySquad(e.currentTarget.checked)}/>
                    </Col>
                </Row>
                <table className="w-full">
                    <thead>

                    <tr>
                        <th>Material</th>
                        <th>Cost per unit</th>
                        <th colSpan={miningShips.length}>Cost per cycle</th>
                    </tr>
                    <tr>
                        <th colSpan={2}/>
                        {miningShips.map(s => <td key={s.id} className="text-right">{s.name}</td>)}
                    </tr>
                    </thead>
                    <tbody>
                    {materials.map(rm => <CostRow key={rm.typeID} rm={rm} mpsf={mpsf} ships={miningShips} bySquad={mineBySquad}/>)}
                    </tbody>
                </table>

            </Paper>

            <Paper mb={10}>
                <Title order={2}>Mining Time</Title>
                <p>This calculations are made assuming max yield, for a base time of 72 hours</p>
                <Row mb={2}>
                    <Col>
                        <Label>Freighter Piloting</Label>
                        <NumberInput value={piloting} onChange={setPiloting} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>Space Command</Label>
                        <NumberInput value={spaceCommand} onChange={setSpaceCommand} min={0} max={5} style={{width: 80}}/>
                    </Col>
                    <Col>
                        <Label>Management</Label>
                        <NumberInput value={management} onChange={setManagement} min={0} max={5} style={{width: 80}}/>
                    </Col>
                </Row>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th>Actions</th>
                                <th>Mining Time</th>
                            </tr>
                            </thead>
                            <tbody>
                            {actions.map(a => {
                                let h = 72 * calculateCDF(a, spaceCommand, management, piloting);
                                let d = Duration.fromObject({hours: h}).shiftTo('hours', 'minutes', 'seconds');
                                return (
                                <tr key={a}>
                                    <th>{a}</th>
                                    <td className="text-center">{d.toHuman()}</td>
                                </tr>
                            )})}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} data={chartMiningTime} series={miningTimeSeries} dataKey="actions" curveType="linear" xAxisLabel="Actions" yAxisLabel="Hours" yAxisProps={{domain: miningTimeYDomain}} />
                    </Col>
                </Row>
            </Paper>
        </div>
    );

}

const deposits = {
    min: 20,
    max: 29
}

const RMChanceRow = ({rm}) => {
    return (
        <tr>
            <th className="capitalize text-left">{rm.name}</th>
            <td>{rm.rarity}</td>
            <td>{rm.type}</td>
            {types.map(t => <RMChanceCell key={t} rm={rm} type={t} />)}
        </tr>
    );
}
const RMChanceCell = ({rm, type}) => {
    let perc = calculateRMChance(rm, type);

    let min = Math.floor(deposits.min * perc);
    let max = Math.ceil(deposits.max * perc);

    return (
        <td className="text-right" title={`${min} - ${max} deposits`}>{percentage.format(perc*100)}%</td>
    )
}

const RMRow = ({rm, mpsf, ships, bySquad}) => {
    return (
        <tr>
            <th className="capitalize text-left">{rm.name}</th>
            <td>{rm.rarity}</td>
            <td className="text-right">{formatter.format(rm.rarity * 0.1 * 550)}</td>
            <td className="text-right">{formatter.format(rm.rarity * 1.5 * 550)}</td>
            {ships.map(ship => <td key={ship.id} className="text-right">{formatter.format(calculateMaxYield(rm.rarity, ship.asteroidMining.mining*(bySquad ? Math.floor(12/ship.slotsize) : 1), mpsf))}</td>)}
        </tr>
    )
}



const CostRow = ({rm, mpsf, ships, bySquad}) => {
    const perUnitCost = calculatePriceUnit(rm, BASE_MINING_POWER, mpsf);
    return <tr>
        <th className="capitalize text-left">{rm.name}</th>
        <td className="text-right">{formatter.format(perUnitCost)}</td>
        {ships.map(ship => {
            const power = ship.asteroidMining.mining*(bySquad ? Math.floor(12/ship.slotsize) : 1)
            const cost = calculatePriceUnit(rm, power, mpsf)
            const amount = calculateMaxYield(rm.rarity, power, mpsf)

            return (
                <td key={ship.id} className="text-right">{formatter.format(cost * amount)}</td>
            )}
        )}
    </tr>
}
